import { Injectable } from "@angular/core";
import {
  MipsPacket,
  MipsQuestionnaire,
  TinEligibilityDetails,
  MipsSettings,
  QualityCategory,
  PromotingInteroperability,
  ImprovementActivities,
  DrcfStatus,
} from "@models/mips-stepper.model";
import { DashboardMeasureDetailProd } from "@models/dashboard-measure-details.model";
import {
  getMipsPerformanceDates,
  DRCF_SIGNING_ENABLED,
  isPdmpExclusionMissing,
  PDMP_EXCLUSION_REQUIRED_MESSAGE,
} from "@constants/mips-constants";
import { getMipsBlockReasonName } from "@constants/mips-block-reasons";
import measureDataJson from "../components/dashboard/measureData.json";

export type ValidationSeverity = "error" | "warning";

export interface ValidationIssue {
  severity: ValidationSeverity;
  message: string;
  field?: string;
}

export interface StepValidationResult {
  stepNumber: number;
  stepName: string;
  isValid: boolean;
  canProceed: boolean;
  canSubmit: boolean;
  issues: ValidationIssue[];
}

export interface ValidationContext {
  packet: MipsPacket | null;
  tinDetails: TinEligibilityDetails | undefined;
  settings: any;
  quality: any;
  pi: any;
  ia: any;
  drcf: any;
  multiPacketMode: any;
  traditionalIA?: any;
  mvpIA?: any;
  questionnaire?: MipsQuestionnaire | null;
  dashboardMeasures?: DashboardMeasureDetailProd[];
  submissionBlock?: { isBlocked: boolean; reasonCodes: string[] };
}

// Sentinel stepNumber distinguishing the admin block "step" from wizard steps 1-8.
export const SUBMISSION_BLOCK_STEP_NUMBER = 0;

@Injectable({
  providedIn: "root",
})
export class MipsValidationService {
  /**
   * Validates all steps and returns results for each
   */
  validateAllSteps(context: ValidationContext): StepValidationResult[] {
    const step4 = this.validateStep4Settings(context);
    const cehrtIssues = this.validateCehrtIdRequired(context);
    if (cehrtIssues.length > 0) {
      step4.issues.push(...cehrtIssues);
      step4.isValid = false;
      step4.canSubmit = false;
    }

    return [
      this.validateStep1Questionnaire(context),
      this.validateStep2Eligibility(context),
      this.validateStep3TinDetails(context),
      step4,
      this.validateStep5Quality(context),
      this.validateStep6PI(context),
      this.validateStep7IA(context),
      this.validateStep8DRCF(context),
      this.validateSubmissionBlock(context),
    ];
  }

  /**
   * Validates a specific step
   */
  validateStep(
    stepNumber: number,
    context: ValidationContext,
  ): StepValidationResult {
    switch (stepNumber) {
      case 1:
        return this.validateStep1Questionnaire(context);
      case 2:
        return this.validateStep2Eligibility(context);
      case 3:
        return this.validateStep3TinDetails(context);
      case 4:
        return this.validateStep4Settings(context);
      case 5:
        return this.validateStep5Quality(context);
      case 6:
        return this.validateStep6PI(context);
      case 7:
        return this.validateStep7IA(context);
      case 8:
        return this.validateStep8DRCF(context);
      case SUBMISSION_BLOCK_STEP_NUMBER:
        return this.validateSubmissionBlock(context);
      default:
        return {
          stepNumber,
          stepName: "Unknown",
          isValid: false,
          canProceed: false,
          canSubmit: false,
          issues: [{ severity: "error", message: "Invalid step number" }],
        };
    }
  }

  /**
   * Checks if packet can be submitted (all required validations pass)
   */
  canSubmitPacket(context: ValidationContext): boolean {
    const results = this.validateAllSteps(context);
    return results.every((r) => r.canSubmit);
  }

  /**
   * Gets all blocking issues that prevent submission
   */
  getBlockingIssues(
    context: ValidationContext,
  ): Array<{ stepNumber: number; stepName: string; issue: ValidationIssue }> {
    const results = this.validateAllSteps(context);
    const blocking: Array<{
      stepNumber: number;
      stepName: string;
      issue: ValidationIssue;
    }> = [];

    results.forEach((result) => {
      if (!result.canSubmit) {
        result.issues
          .filter((issue) => issue.severity === "error")
          .forEach((issue) => {
            blocking.push({
              stepNumber: result.stepNumber,
              stepName: result.stepName,
              issue,
            });
          });
      }
    });

    return blocking;
  }

  getExcludedCategories(questionnaire: MipsQuestionnaire | null | undefined): Set<string> {
    const excluded = new Set<string>();
    if (!questionnaire) return excluded;

    if (questionnaire.appliedHardship === "yes") {
      if (questionnaire.hardshipQuality === "yes") excluded.add("quality");
      if (questionnaire.hardshipPromotingInteroperability === "yes") excluded.add("pi");
      if (questionnaire.hardShipImprovementActivities === "yes") excluded.add("ia");
    }

    const thirdParty = this.getThirdPartyCategories(questionnaire);
    thirdParty.forEach((category) => excluded.add(category));

    return excluded;
  }

  getThirdPartyCategories(questionnaire: MipsQuestionnaire | null | undefined): Set<string> {
    const categories = new Set<string>();
    if (!questionnaire || questionnaire.thirdPartyData !== "yes") return categories;

    if (questionnaire.thirdPartyDataQuality === "yes") categories.add("quality");
    if (questionnaire.thirdPartyDataPromotingInteroperability === "yes") categories.add("pi");
    if (questionnaire.thirdPartyDataImprovementActivities === "yes") categories.add("ia");

    return categories;
  }

  // ============================================
  // STEP 1: QUESTIONNAIRE
  // ============================================

  private validateStep1Questionnaire(
    context: ValidationContext,
  ): StepValidationResult {
    const issues: ValidationIssue[] = [];

    if (!context.packet) {
      issues.push({
        severity: "error",
        message: "No submission created",
      });
    }

    const isValid = issues.length === 0;

    return {
      stepNumber: 1,
      stepName: "Questionnaire",
      isValid,
      canProceed: isValid,
      canSubmit: isValid,
      issues,
    };
  }

  // ============================================
  // STEP 2: ELIGIBILITY
  // ============================================

  private validateStep2Eligibility(
    context: ValidationContext,
  ): StepValidationResult {
    const issues: ValidationIssue[] = [];
    const { packet } = context;

    if (!packet) {
      issues.push({
        severity: "error",
        message: "No submission available",
      });
      return {
        stepNumber: 2,
        stepName: "Eligibility",
        isValid: false,
        canProceed: false,
        canSubmit: false,
        issues,
      };
    }

    if (!packet.npi) {
      issues.push({
        severity: "error",
        message: "NPI is required",
        field: "npi",
      });
    }

    if (!packet.reportingType) {
      issues.push({
        severity: "error",
        message: "Reporting type is required",
        field: "reportingType",
      });
    }

    if (!packet.reportingLevel) {
      issues.push({
        severity: "error",
        message: "Reporting level is required",
        field: "reportingLevel",
      });
    }

    const canProceedToNext =
      issues.filter((i) => i.severity === "error").length === 0;

    const isMvpIdMissing = packet.reportingType === "mvp" && !packet.mvpId;
    if (isMvpIdMissing) {
      issues.push({
        severity: "error",
        message:
          "MVP ID is required before this submission can be finalized. Re-check MVP registration.",
        field: "mvpId",
      });
    }

    const canSubmit = canProceedToNext && !isMvpIdMissing;

    return {
      stepNumber: 2,
      stepName: "Eligibility",
      isValid: canSubmit,
      canProceed: canProceedToNext,
      canSubmit,
      issues,
    };
  }

  // ============================================
  // STEP 3: TIN DETAILS
  // ============================================

  private validateStep3TinDetails(
    context: ValidationContext,
  ): StepValidationResult {
    const issues: ValidationIssue[] = [];
    const { tinDetails } = context;

    if (!tinDetails) {
      issues.push({
        severity: "error",
        message: "TIN details not provided",
      });
      return {
        stepNumber: 3,
        stepName: "TIN Details",
        isValid: false,
        canProceed: false,
        canSubmit: false,
        issues,
      };
    }

    if (!tinDetails.tin) {
      issues.push({
        severity: "error",
        message: "TIN is required",
        field: "tin",
      });
    }

    if (!tinDetails.performanceStartDate) {
      issues.push({
        severity: "error",
        message: "Performance start date is required",
        field: "performanceStartDate",
      });
    }

    if (!tinDetails.performanceEndDate) {
      issues.push({
        severity: "error",
        message: "Performance end date is required",
        field: "performanceEndDate",
      });
    }

    if (!tinDetails.emailForFeedback) {
      issues.push({
        severity: "error",
        message: "Email for feedback is required",
        field: "emailForFeedback",
      });
    }

    const canProceedToNext =
      issues.filter((i) => i.severity === "error").length === 0;

    if (tinDetails.tinStatus !== "approved") {
      issues.push({
        severity: "error",
        message: `TIN status is "${tinDetails.tinStatus}" - must be "approved" before submission`,
      });
    }

    const canSubmit =
      tinDetails.tinStatus === "approved" &&
      issues.filter(
        (i) =>
          i.message !==
          `TIN status is "${tinDetails.tinStatus}" - must be "approved" before submission`,
      ).length === 0;

    return {
      stepNumber: 3,
      stepName: "TIN Details",
      isValid: canSubmit,
      canProceed: canProceedToNext,
      canSubmit: canSubmit,
      issues,
    };
  }

  // ============================================
  // STEP 4: SETTINGS
  // ============================================

  private validateStep4Settings(
    context: ValidationContext,
  ): StepValidationResult {
    const issues: ValidationIssue[] = [];
    const { settings } = context;

    if (!settings) {
      issues.push({
        severity: "error",
        message: "Settings not provided",
      });
      return {
        stepNumber: 4,
        stepName: "Settings",
        isValid: false,
        canProceed: false,
        canSubmit: false,
        issues,
      };
    }

    const isValid = issues.length === 0;

    return {
      stepNumber: 4,
      stepName: "Settings",
      isValid,
      canProceed: isValid,
      canSubmit: isValid,
      issues,
    };
  }

  /**
   * Checks if CEHRT ID is required based on PI submission or CMS eCQM quality measures.
   * Returns validation issues if cehrtId is missing but required.
   */
  private validateCehrtIdRequired(context: ValidationContext): ValidationIssue[] {
    const { settings, quality } = context;

    if (settings?.cehrtId) {
      return [];
    }

    const excluded = this.getExcludedCategories(context.questionnaire);
    const isSubmittingPI = !excluded.has("pi");

    const selectedIds = quality?.selectedMeasureIds || [];

    // Check quality measures from the store for measureType
    const allQualityMeasures = [
      ...(quality?.traditionalMeasures || []),
      ...(quality?.mvpMeasures || []),
    ];
    let hasEcqmMeasure = allQualityMeasures
      .filter((m: any) => selectedIds.includes(m.measureId))
      .some((m: any) => m.measureType === "CMS eCQM");

    // Fallback: check dashboard measures if quality measures don't have measureType
    if (!hasEcqmMeasure && selectedIds.length > 0) {
      const dashboardMeasures = context.dashboardMeasures || [];
      hasEcqmMeasure = dashboardMeasures
        .filter((m) => {
          const baseMeasureId = this.getBaseMeasureId(m.name || m.id || "");
          return selectedIds.includes(baseMeasureId);
        })
        .some((m) => m.measureType === "CMS eCQM");
    }

    const issues: ValidationIssue[] = [];

    if (isSubmittingPI || hasEcqmMeasure) {
      const reasons: string[] = [];
      if (isSubmittingPI) reasons.push("Promoting Interoperability");
      if (hasEcqmMeasure) reasons.push("CMS eCQM quality measures");

      issues.push({
        severity: "error",
        message: `CEHRT ID is required when submitting ${reasons.join(" or ")}`,
        field: "cehrtId",
      });
    }

    return issues;
  }

  // ============================================
  // STEP 5: QUALITY
  // ============================================

  private validateStep5Quality(
    context: ValidationContext,
  ): StepValidationResult {
    const excluded = this.getExcludedCategories(context.questionnaire);
    if (excluded.has("quality")) {
      return {
        stepNumber: 5,
        stepName: "Quality",
        isValid: true,
        canProceed: true,
        canSubmit: true,
        issues: [],
      };
    }

    const issues: ValidationIssue[] = [];
    const { packet, quality, multiPacketMode } = context;

    if (!packet) {
      issues.push({
        severity: "error",
        message: "No submission available",
      });
      return {
        stepNumber: 5,
        stepName: "Quality",
        isValid: false,
        canProceed: false,
        canSubmit: false,
        issues,
      };
    }

    const reportingType =
      multiPacketMode?.enabled && multiPacketMode?.mode
        ? multiPacketMode.mode
        : packet.reportingType || "traditional";

    if (reportingType === "both") {
      const traditionalResult = this.validateQualityMeasures(
        quality,
        "traditional",
        3,
        "Traditional",
      );
      const mvpResult = this.validateQualityMeasures(quality, "mvp", 3, "MVP");

      issues.push(...traditionalResult.issues);
      issues.push(...mvpResult.issues);

      const canProceed = traditionalResult.canProceed && mvpResult.canProceed;
      const canSubmit = traditionalResult.canSubmit && mvpResult.canSubmit;

      return {
        stepNumber: 5,
        stepName: "Quality",
        isValid: canSubmit,
        canProceed,
        canSubmit,
        issues,
      };
    }

    const minMeasures = 3;
    const modeLabel = reportingType === "mvp" ? "MVP" : "Traditional";

    const result = this.validateQualityMeasures(
      quality,
      reportingType,
      minMeasures,
      modeLabel,
    );

    return {
      stepNumber: 5,
      stepName: "Quality",
      isValid: result.canSubmit,
      canProceed: result.canProceed,
      canSubmit: result.canSubmit,
      issues: result.issues,
    };
  }

  private validateQualityMeasures(
    quality: any,
    mode: "traditional" | "mvp",
    minMeasures: number,
    modeLabel: string,
  ): { canProceed: boolean; canSubmit: boolean; issues: ValidationIssue[] } {
    const issues: ValidationIssue[] = [];

    if (!quality) {
      issues.push({
        severity: "error",
        message: `${modeLabel}: No quality data available`,
      });
      return { canProceed: false, canSubmit: false, issues };
    }

    let selectedCount = 0;
    if (mode === "mvp") {
      selectedCount = quality.mvpMeasuresSelected || 0;
    } else {
      selectedCount = quality.traditionalMeasuresSelected || 0;
    }

    if (selectedCount === 0) {
      issues.push({
        severity: "error",
        message: `${modeLabel}: No quality measures selected`,
        field: "selectedMeasures",
      });
      return { canProceed: false, canSubmit: false, issues };
    }

    if (selectedCount < minMeasures) {
      issues.push({
        severity: "error",
        message: `${modeLabel}: Need ${minMeasures} measures, only ${selectedCount} selected`,
        field: "measureCount",
      });
    }

    const allMeasures =
      mode === "mvp"
        ? quality.mvpMeasures || []
        : quality.traditionalMeasures || [];
    const allSelectedMeasureIds = quality.selectedMeasureIds || [];

    const selectedMeasures = allMeasures.filter((m: any) =>
      allSelectedMeasureIds.includes(m.measureId),
    );

    const hasOutcomeHighPriority = selectedMeasures.some(
      (m: { isOutcome: boolean; isHighPriority: boolean }) =>
        m.isOutcome === true || m.isHighPriority === true,
    );

    if (!hasOutcomeHighPriority) {
      issues.push({
        severity: "error",
        message: `${modeLabel}: Must include at least one Outcome or High-Priority measure`,
        field: "outcomeHighPriority",
      });
    }
    const equivalentIssues = this.validateEquivalentMeasures(
      selectedMeasures,
      modeLabel,
    );
    issues.push(...equivalentIssues);

    const errorIssues = issues.filter((i) => i.severity === "error");
    const canProceed = selectedCount > 0;
    const canSubmit = errorIssues.length === 0;

    return { canProceed, canSubmit, issues };
  }

  /**
   * Validates that equivalent measures are not both selected
   */
  private validateEquivalentMeasures(
    selectedMeasures: any[],
    modeLabel: string,
  ): ValidationIssue[] {
    const issues: ValidationIssue[] = [];

    const selectedBaseMeasureIds = selectedMeasures.map((m) =>
      this.getBaseMeasureId(m.measureId),
    );

    const equivalentPairs = (measureDataJson as any).equivalentMeasures || [];

    equivalentPairs.forEach((pair: any) => {
      const hasFirstMeasure = selectedBaseMeasureIds.includes(pair.measureId);
      const hasSecondMeasure = selectedBaseMeasureIds.includes(pair.cmsEcqmId);

      if (hasFirstMeasure && hasSecondMeasure) {
        issues.push({
          severity: "error",
          message: `${modeLabel}: Cannot select both equivalent measures ${pair.measureId} and ${pair.cmsEcqmId}`,
          field: "equivalentMeasures",
        });
      }
    });

    return issues;
  }

  private getBaseMeasureId(measureName: string): string {
    if (!measureName) return "";
    const cleanName = measureName.replace(/_\d{4}$/g, "");
    if (/^[a-zA-Z]+-\d+-\d+$/.test(cleanName)) {
      return cleanName.replace(/-\d+$/, "");
    }
    return cleanName;
  }

  // ============================================
  // STEP 6: PROMOTING INTEROPERABILITY (PI)
  // ============================================

  private validateStep6PI(context: ValidationContext): StepValidationResult {
    const excluded = this.getExcludedCategories(context.questionnaire);
    if (excluded.has("pi")) {
      return {
        stepNumber: 6,
        stepName: "Promoting Interoperability",
        isValid: true,
        canProceed: true,
        canSubmit: true,
        issues: [],
      };
    }

    const issues: ValidationIssue[] = [];
    const { pi } = context;

    if (!pi) {
      issues.push({
        severity: "error",
        message: "Promoting Interoperability data not available",
      });
      return {
        stepNumber: 6,
        stepName: "Promoting Interoperability",
        isValid: false,
        canProceed: false,
        canSubmit: false,
        issues,
      };
    }

    if (
      !pi.attestations ||
      !this.checkPIAttestationsComplete(pi.attestations, context.packet?.performanceYear)
    ) {
      issues.push({
        severity: "error",
        message: "All PI attestations must be answered 'Yes'",
      });
    }

    if (pi.allRequiredComplete !== true) {
      issues.push({
        severity: "error",
        message: "All required PI measures must be completed",
      });
    }

    if (isPdmpExclusionMissing(context.packet?.performanceYear, pi.allExclusionIds || [])) {
      issues.push({
        severity: "error",
        message: PDMP_EXCLUSION_REQUIRED_MESSAGE,
      });
    }

    const incompleteMeasureIds: string[] = pi.incompleteMeasureIds || [];

    if (incompleteMeasureIds.length > 0) {
      const measureList = incompleteMeasureIds.join(", ");
      issues.push({
        severity: "error",
        message: `${incompleteMeasureIds.length} selected PI measure${incompleteMeasureIds.length === 1 ? " is" : "s are"} incomplete: ${measureList}`,
        field: "incompleteMeasures",
      });
    }

    if (!pi.performanceStart || !pi.performanceEnd) {
      issues.push({
        severity: "error",
        message: "Performance dates are required",
      });
    }

    const canSubmit = issues.length === 0;

    return {
      stepNumber: 6,
      stepName: "Promoting Interoperability",
      isValid: canSubmit,
      canProceed: canSubmit,
      canSubmit,
      issues,
    };
  }

  private checkPIAttestationsComplete(attestations: any, performanceYear?: number | null): boolean {
    if (!attestations) return false;
    if (attestations.piAuditAttestation !== true) return false;

    const baseComplete =
      attestations.oncDirectReview === true &&
      attestations.interoperabilityAttestation === true &&
      attestations.securityRiskAnalysis === true &&
      attestations.saferGuidesAttestation === true;

    if (performanceYear != null && performanceYear >= 2026) {
      return baseComplete && attestations.securityRiskAnalysisImplemented === true;
    }
    return baseComplete;
  }

  // ============================================
  // STEP 7: IA
  // ============================================

  private validateStep7IA(context: ValidationContext): StepValidationResult {
    const excluded = this.getExcludedCategories(context.questionnaire);
    if (excluded.has("ia")) {
      return {
        stepNumber: 7,
        stepName: "Improvement Activities",
        isValid: true,
        canProceed: true,
        canSubmit: true,
        issues: [],
      };
    }

    const issues: ValidationIssue[] = [];
    const { ia, multiPacketMode } = context;

    if (!ia && !context.traditionalIA && !context.mvpIA) {
      issues.push({
        severity: "error",
        message: "Improvement Activities data not available",
      });
      return {
        stepNumber: 7,
        stepName: "Improvement Activities",
        isValid: false,
        canProceed: false,
        canSubmit: false,
        issues,
      };
    }
    const iaForDates = ia || context.traditionalIA || context.mvpIA;

    if (!iaForDates?.dateRangeStart || !iaForDates?.dateRangeEnd) {
      issues.push({
        severity: "error",
        message: "Performance dates are required",
        field: "performanceDates",
      });
    }

    if (iaForDates?.dateRangeStart && iaForDates?.dateRangeEnd) {
      const startDate = new Date(iaForDates.dateRangeStart);
      const endDate = new Date(iaForDates.dateRangeEnd);
      const { MIN_DATE, MAX_DATE } = getMipsPerformanceDates();
      const perfYear = MAX_DATE.getFullYear();

      if (
        startDate < MIN_DATE ||
        startDate > MAX_DATE
      ) {
        issues.push({
          severity: "error",
          message: `Performance start date must be within ${perfYear}`,
          field: "dateRangeStart",
        });
      }

      if (
        endDate < MIN_DATE ||
        endDate > MAX_DATE
      ) {
        issues.push({
          severity: "error",
          message: `Performance end date must be within ${perfYear}`,
          field: "dateRangeEnd",
        });
      }

      if (startDate > endDate) {
        issues.push({
          severity: "error",
          message: "Performance start date must be before end date",
          field: "dateRange",
        });
      }
    }

    if (multiPacketMode?.enabled && (context.traditionalIA || context.mvpIA)) {
      const tradResult = this.validateIAPoints(context.traditionalIA, "Traditional");
      const mvpResult = this.validateIAPoints(context.mvpIA, "MVP");

      issues.push(...tradResult.issues);
      issues.push(...mvpResult.issues);

      const canSubmit = issues.filter((i) => i.severity === "error").length === 0;

      return {
        stepNumber: 7,
        stepName: "Improvement Activities",
        isValid: canSubmit,
        canProceed: canSubmit,
        canSubmit,
        issues,
      };
    }

    const estimatedTotal = Math.min(ia?.estimatedPoints || 0, 40);

    if (estimatedTotal < 40) {
      issues.push({
        severity: "error",
        message: `Must achieve 40 points in Improvement Activities. Current: ${estimatedTotal} points`,
        field: "minimumPoints",
      });
    }

    const canSubmit = issues.length === 0;

    return {
      stepNumber: 7,
      stepName: "Improvement Activities",
      isValid: canSubmit,
      canProceed: canSubmit,
      canSubmit,
      issues,
    };
  }

  private validateIAPoints(
    ia: any,
    modeLabel: string,
  ): { issues: ValidationIssue[] } {
    const issues: ValidationIssue[] = [];

    if (!ia) {
      issues.push({
        severity: "error",
        message: `${modeLabel}: No Improvement Activities data available`,
      });
      return { issues };
    }

    const estimatedTotal = Math.min(ia.estimatedPoints || 0, 40);

    if (estimatedTotal < 40) {
      issues.push({
        severity: "error",
        message: `${modeLabel}: Must achieve 40 points in Improvement Activities. Current: ${estimatedTotal} points`,
        field: "minimumPoints",
      });
    }

    return { issues };
  }

  // ============================================
  // STEP 8: DRCF
  // ============================================

  private validateStep8DRCF(context: ValidationContext): StepValidationResult {
    if (!DRCF_SIGNING_ENABLED) {
      return {
        stepNumber: 8,
        stepName: "DRCF",
        isValid: false,
        canProceed: false,
        canSubmit: false,
        issues: [
          {
            severity: "error",
            message:
              "DRCF signing is temporarily disabled for this performance year. Submission is blocked until DRCF reopens.",
          },
        ],
      };
    }

    const issues: ValidationIssue[] = [];
    const { drcf } = context;

    const documents = drcf?.documents || [];

    if (documents.length === 0) {
      issues.push({
        severity: "error",
        message: "No DRCF documents have been created",
      });
    } else if (!drcf?.allDocumentsSigned) {
      issues.push({
        severity: "error",
        message: "Not all DRCF documents are signed",
      });
    }

    const isValid = issues.length === 0;

    return {
      stepNumber: 8,
      stepName: "DRCF",
      isValid,
      canProceed: isValid,
      canSubmit: isValid,
      issues,
    };
  }

  // ============================================
  // SUBMISSION BLOCK (admin-enforced, Admin Hub)
  // ============================================

  private validateSubmissionBlock(context: ValidationContext): StepValidationResult {
    const isBlocked = context.submissionBlock?.isBlocked ?? false;

    if (!isBlocked) {
      return {
        stepNumber: SUBMISSION_BLOCK_STEP_NUMBER,
        stepName: "Submission",
        isValid: true,
        canProceed: true,
        canSubmit: true,
        issues: [],
      };
    }

    const reasonCodes = context.submissionBlock?.reasonCodes ?? [];
    const reasonsText = reasonCodes.map((code) => getMipsBlockReasonName(code)).join(", ");

    return {
      stepNumber: SUBMISSION_BLOCK_STEP_NUMBER,
      stepName: "Submission",
      isValid: false,
      canProceed: false,
      canSubmit: false,
      issues: [
        {
          severity: "error",
          message: `Submission cannot be completed due to ${reasonsText}. Please contact the Reg-ENT Team, regent@entnet.org, for further assistance.`,
        },
      ],
    };
  }
}
